import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";
import Seo from "../../seo";
import { api, formatApiError } from "../../api";

const EMPTY = {
    title: "", slug: "", category: "General Urology", summary: "", author: "Dr. Krishnakanth AVB",
    date: "", reading_time: 5, image_url: "", content: "", references: "",
    seo_title: "", meta_description: "", related: "", published: true,
};

export default function ArticleEditor() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(slug);
    const [form, setForm] = useState(EMPTY);
    const [categories, setCategories] = useState([]);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(isEdit);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/admin/articles");
                setCategories(r.data.categories);
                if (isEdit) {
                    const a = r.data.articles.find((x) => x.slug === slug);
                    if (!a) { navigate("/admin/dashboard"); return; }
                    setForm({
                        ...EMPTY, ...a,
                        content: (a.content || []).join("\n\n"),
                        references: (a.references || []).join("\n"),
                        related: (a.related || []).join(", "),
                    });
                }
            } catch {
                navigate("/admin");
            } finally {
                setLoading(false);
            }
        })();
    }, [slug, isEdit, navigate]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const payload = {
            ...form,
            reading_time: parseInt(form.reading_time, 10) || 5,
            content: form.content.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean),
            references: form.references.split("\n").map((r) => r.trim()).filter(Boolean),
            related: form.related.split(",").map((s) => s.trim()).filter(Boolean),
        };
        try {
            if (isEdit) await api.put(`/admin/articles/${slug}`, payload);
            else await api.post("/admin/articles", payload);
            toast.success(isEdit ? "Article updated." : "Article published.");
            navigate("/admin/dashboard");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const inputCls = "w-full rounded-lg border border-input bg-white px-4 py-3 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-accent";

    if (loading) return <div className="flex min-h-screen items-center justify-center text-steel">Loading…</div>;

    return (
        <div className="min-h-screen bg-mist">
            <Seo title={`${isEdit ? "Edit" : "New"} Article | Admin`} description="Admin area." />
            <div className="mx-auto max-w-3xl px-5 py-10">
                <Link to="/admin/dashboard" data-testid="back-to-dashboard-link"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-steel hover:text-navy">
                    <ArrowLeft className="h-4 w-4" /> Dashboard
                </Link>
                <h1 className="mt-6 font-display text-3xl font-medium text-navy">{isEdit ? "Edit article" : "New article"}</h1>
                <form onSubmit={submit} className="mt-8 space-y-6 rounded-2xl border border-border bg-white p-7" data-testid="article-editor-form">
                    <div>
                        <label className="mb-1.5 block text-xs font-semibold text-navy">Title *</label>
                        <input required value={form.title} onChange={set("title")} className={inputCls} data-testid="article-title-input" />
                    </div>
                    <div className="grid gap-6 sm:grid-cols-2">
                        <div>
                            <label className="mb-1.5 block text-xs font-semibold text-navy">Slug (URL) *</label>
                            <input required value={form.slug} onChange={set("slug")} disabled={isEdit}
                                className={`${inputCls} disabled:bg-mist`} data-testid="article-slug-input"
                                placeholder="my-article-slug" />
                        </div>
                        <div>
                            <label className="mb-1.5 block text-xs font-semibold text-navy">Category</label>
                            <select value={form.category} onChange={set("category")} className={inputCls} data-testid="article-category-select">
                                {(categories.length ? categories : ["General Urology"]).map((c) => <option key={c}>{c}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="mb-1.5 block text-xs font-semibold text-navy">Author</label>
                            <input value={form.author} onChange={set("author")} className={inputCls} data-testid="article-author-input" />
                        </div>
                        <div>
                            <label className="mb-1.5 block text-xs font-semibold text-navy">Publication date</label>
                            <input type="date" value={form.date} onChange={set("date")} className={inputCls} data-testid="article-date-input" />
                        </div>
                        <div>
                            <label className="mb-1.5 block text-xs font-semibold text-navy">Reading time (minutes)</label>
                            <input type="number" min="1" max="60" value={form.reading_time} onChange={set("reading_time")} className={inputCls} data-testid="article-reading-time-input" />
                        </div>
                        <div>
                            <label className="mb-1.5 block text-xs font-semibold text-navy">Featured image URL</label>
                            <input value={form.image_url} onChange={set("image_url")} className={inputCls} data-testid="article-image-input" placeholder="https://…" />
                        </div>
                    </div>
                    <div>
                        <label className="mb-1.5 block text-xs font-semibold text-navy">Short summary</label>
                        <textarea rows={2} value={form.summary} onChange={set("summary")} className={inputCls} data-testid="article-summary-input" />
                    </div>
                    <div>
                        <label className="mb-1.5 block text-xs font-semibold text-navy">Article content — separate paragraphs with a blank line</label>
                        <textarea rows={12} value={form.content} onChange={set("content")} className={inputCls} data-testid="article-content-input" />
                    </div>
                    <div>
                        <label className="mb-1.5 block text-xs font-semibold text-navy">References — one per line</label>
                        <textarea rows={3} value={form.references} onChange={set("references")} className={inputCls} data-testid="article-references-input"
                            placeholder="EAU Guidelines on Urolithiasis" />
                    </div>
                    <div className="grid gap-6 sm:grid-cols-2">
                        <div>
                            <label className="mb-1.5 block text-xs font-semibold text-navy">SEO title (optional)</label>
                            <input value={form.seo_title} onChange={set("seo_title")} className={inputCls} data-testid="article-seo-title-input" />
                        </div>
                        <div>
                            <label className="mb-1.5 block text-xs font-semibold text-navy">Meta description (optional)</label>
                            <input value={form.meta_description} onChange={set("meta_description")} className={inputCls} data-testid="article-meta-desc-input" />
                        </div>
                    </div>
                    <div>
                        <label className="mb-1.5 block text-xs font-semibold text-navy">Related articles — slugs, comma separated</label>
                        <input value={form.related} onChange={set("related")} className={inputCls} data-testid="article-related-input"
                            placeholder="kidney-stones-basics, what-is-a-urethral-stricture" />
                    </div>
                    <label className="flex items-center gap-3 text-sm text-navy">
                        <input type="checkbox" checked={form.published} onChange={set("published")} className="h-4 w-4 accent-[#0A192F]" data-testid="article-published-checkbox" />
                        Published (visible on the website)
                    </label>
                    <button type="submit" disabled={saving} data-testid="article-save-btn"
                        className="inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-semibold text-white hover:bg-accent disabled:opacity-60">
                        <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save article"}
                    </button>
                </form>
            </div>
        </div>
    );
}
